import React, { useEffect, useRef, useState } from 'react';
import { FileText, MessageSquare, Download, Loader2, FolderOpen, RefreshCw } from 'lucide-react';
import {
    getAiAssistantLibrary,
    downloadAiAssistantContextFile,
    downloadAiAssistantFile,
} from '../../utils/APIs';

const LibraryList = ({
    token,
    organizationId,
    projectId,
}) => {
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [filter, setFilter] = useState('all');
    const requestRef = useRef(null);

    const loadLibrary = () => {
        if (!token || !organizationId) return;
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        setIsLoading(true);
        setError(null);

        getAiAssistantLibrary(token, organizationId, projectId, controller.signal)
            .then((result) => {
                if (!controller.signal.aborted) {
                    setFiles(result?.data?.files || []);
                }
            })
            .catch((err) => {
                if (!controller.signal.aborted) {
                    setError(err?.response?.data?.message || err?.message || 'Unable to load project library.');
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });
    };

    useEffect(() => {
        loadLibrary();
        return () => requestRef.current?.abort();
    }, [token, organizationId, projectId]);

    const handleDownload = async (file) => {
        if (downloadingId) return;
        setDownloadingId(file.filename);
        try {
            if (file.source === 'context') {
                await downloadAiAssistantContextFile(token, organizationId, file.filename);
            } else {
                await downloadAiAssistantFile(token, organizationId, file.filename);
            }
        } catch (err) {
            alert(err?.response?.data?.message || err?.message || 'Failed to download file.');
        } finally {
            setDownloadingId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="sai-history__state">
                <Loader2 size={18} className="sai-spin" aria-hidden="true" />
                <span>Loading library files…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="sai-history__state sai-history__state--error flex flex-col gap-2">
                <span>{error}</span>
                <button
                    type="button"
                    className="text-xs text-primary underline flex items-center gap-1 cursor-pointer"
                    onClick={loadLibrary}
                >
                    <RefreshCw size={12} aria-hidden="true" />
                    Retry
                </button>
            </div>
        );
    }

    if (!files.length) {
        return (
            <div className="sai-history__state flex flex-col gap-2 text-center p-8">
                <FolderOpen size={32} className="text-gray-300 mx-auto" aria-hidden="true" />
                <span>No context or conversation files in this project yet.</span>
            </div>
        );
    }

    const filteredFiles = files.filter((f) => {
        if (filter === 'context') return f.source === 'context';
        if (filter === 'conversation') return f.source === 'conversation';
        return true;
    });

    const contextCount = files.filter((f) => f.source === 'context').length;
    const conversationCount = files.filter((f) => f.source === 'conversation').length;

    return (
        <div className="sai-history">
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 mb-2">
                <button
                    type="button"
                    className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                        filter === 'all'
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => setFilter('all')}
                >
                    All ({files.length})
                </button>
                <button
                    type="button"
                    className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                        filter === 'context'
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => setFilter('context')}
                >
                    Context Docs ({contextCount})
                </button>
                <button
                    type="button"
                    className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                        filter === 'conversation'
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => setFilter('conversation')}
                >
                    Conversation ({conversationCount})
                </button>
            </div>

            {filteredFiles.map((file, idx) => {
                const isDownloading = downloadingId === file.filename;
                const isContext = file.source === 'context';

                return (
                    <div
                        key={`${file.source}-${file.filename}-${idx}`}
                        className="sai-history__item"
                    >
                        <div className="sai-history__open">
                            <span className="sai-history__icon">
                                {isContext ? (
                                    <FileText size={16} className="text-primary" aria-hidden="true" />
                                ) : (
                                    <MessageSquare size={16} className="text-purple-600" aria-hidden="true" />
                                )}
                            </span>
                            <span className="sai-history__body">
                                <span className="sai-history__title font-medium" title={file.name || file.filename}>
                                    {file.name || file.filename}
                                </span>
                                <span className="sai-history__meta flex items-center gap-2">
                                    <span
                                        className={`inline-block px-1.5 py-0.2 rounded text-[10px] font-semibold ${
                                            isContext
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'bg-purple-50 text-purple-700'
                                        }`}
                                    >
                                        {isContext ? 'Context Doc' : 'Conversation'}
                                    </span>
                                    {file.conversation_title && (
                                        <span className="truncate text-gray-400" title={file.conversation_title}>
                                            · {file.conversation_title}
                                        </span>
                                    )}
                                </span>
                            </span>
                        </div>
                        <button
                            type="button"
                            className="sai-history__delete text-primary hover:text-primary !opacity-100 flex items-center justify-center p-2"
                            onClick={() => handleDownload(file)}
                            disabled={isDownloading}
                            title={`Download ${file.name || file.filename}`}
                            aria-label={`Download ${file.name || file.filename}`}
                        >
                            {isDownloading ? (
                                <Loader2 size={15} className="sai-spin text-primary" aria-hidden="true" />
                            ) : (
                                <Download size={15} aria-hidden="true" />
                            )}
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export default LibraryList;
